import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';
import { formatDate, relativeTime } from '@/utils/format';

/**
 * Moderation, and deliberately very little of it.
 *
 * The list shows who is talking and how much. It does not show what they
 * said, and the API will not return it — a conversation opens to moderation
 * when one of its participants reports it, and not before. Opening a reported
 * thread is audited.
 */
const TABS = [
  { key: 'reported', label: 'Reported' },
  { key: 'all', label: 'Everything' },
];

const PAGE_SIZE = 20;

export function AdminMessagesPage() {
  const [page, setPage] = useState(1);
  const [reportedOnly, setReportedOnly] = useState(true);
  const [openRow, setOpenRow] = useState(null);
  const openId = openRow?.id ?? null;

  const conversations = useQuery({
    queryKey: ['admin-conversations', page, reportedOnly],
    queryFn: () =>
      adminApi.listConversations({ page, page_size: PAGE_SIZE, reported: reportedOnly }),
  });

  const thread = useQuery({
    queryKey: ['admin-conversation', openId],
    queryFn: () => adminApi.getReportedConversation(openId),
    enabled: Boolean(openId),
  });

  const rows = conversations.data?.data ?? [];
  const meta = conversations.data?.meta;

  return (
    <div>
      <PageHeader
        title="Messages"
        description="Who is talking to whom, and the conversations somebody has reported."
      />

      {/* The privacy rule, stated where the list is -- what the old card
          header said, now above the card since the tabs name the list. */}
      <Alert tone="info" className="mb-6">
        Message contents are private. A conversation can only be read once one of its participants
        reports it, and reading it is recorded in the audit log.
      </Alert>

      <ListCard
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS}
            value={reportedOnly ? 'reported' : 'all'}
            onChange={(key) => {
              setReportedOnly(key === 'reported');
              setPage(1);
            }}
            label="Conversations"
          />
        }
        state={
          conversations.isError ? (
            <ErrorState
              message={errorMessage(conversations.error)}
              onRetry={() => void conversations.refetch()}
            />
          ) : conversations.isPending ? (
            <LoadingState label="Loading conversations…" />
          ) : rows.length === 0 ? (
            <EmptyState
              title={reportedOnly ? 'Nothing reported' : 'No conversations yet'}
              description={
                reportedOnly
                  ? 'Nobody has reported a conversation.'
                  : 'No company and candidate have started talking.'
              }
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              accent={row.is_reported ? 'danger' : 'neutral'}
              leading={<BoxAvatar name={row.company_name} />}
              title={row.company_name}
              badges={
                row.is_reported ? (
                  <Badge dot tone="danger">
                    Reported
                  </Badge>
                ) : (
                  <Badge tone="neutral">Private</Badge>
                )
              }
              subtitle={`With ${row.student_name}`}
              facts={[
                {
                  icon: 'chat',
                  text: `${row.message_count} ${row.message_count === 1 ? 'message' : 'messages'}`,
                },
                { icon: 'calendar', text: `Started ${formatDate(row.created_at)}` },
                {
                  icon: 'clockOutline',
                  text: `Last activity ${relativeTime(row.last_message_at)}`,
                },
              ]}
              actions={
                row.is_reported && (
                  <Button
                    variant="tintBrand"
                    size="sm"
                    onClick={() => setOpenRow(row)}
                    title={`Read the reported conversation between ${row.company_name} and ${row.student_name}`}
                  >
                    <Icon name="eye" size={14} />
                    Read conversation
                  </Button>
                )
              }
            >
              {row.is_reported && <ReportReason row={row} />}
            </BoxRow>
          ))}
        </BoxList>
      </ListCard>

      <Modal
        open={Boolean(openId)}
        title="Reported conversation"
        description="Opening this has been recorded in the audit log. Neither participant is told that it was read."
        onClose={() => setOpenRow(null)}
        footer={
          <Button variant="secondary" onClick={() => setOpenRow(null)}>
            Close
          </Button>
        }
      >
        {openRow?.is_reported && <ReportReason row={openRow} className="mb-4" />}
        {thread.isPending ? (
          <LoadingState label="Loading…" />
        ) : thread.isError ? (
          <ErrorState message={errorMessage(thread.error)} onRetry={() => void thread.refetch()} />
        ) : (
          <div className="max-h-96 space-y-3 overflow-y-auto">
            {thread.data.messages.map((message) => (
              <div key={message.id} className="border-line rounded border p-2">
                <p className="text-ink-muted text-xs">
                  {message.sender_name} · {message.sender_side.toLowerCase()} ·{' '}
                  {formatDate(message.created_at)}
                </p>
                <p className="text-ink mt-1 text-sm whitespace-pre-wrap">{message.body}</p>
                {message.attachments.length > 0 && (
                  <p className="text-ink-muted mt-1 text-xs">
                    {message.attachments.length} attachment
                    {message.attachments.length === 1 ? '' : 's'} (not opened here)
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

/** What the participant said was wrong, who said it, and when. */
function ReportReason({ row, className }) {
  const who =
    row.reported_by_side === 'STUDENT'
      ? `${row.reported_by_name ?? 'The student'} (student)`
      : row.reported_by_side === 'COMPANY'
        ? `${row.reported_by_name ?? 'The company'} (company)`
        : null;
  return (
    <div
      className={cn(
        'border-danger-200 bg-danger-50 rounded-lg border px-3 py-2.5',
        className ?? 'mt-1',
      )}
    >
      <p className="text-danger-700 flex flex-wrap items-center gap-x-1.5 text-xs font-semibold">
        <Icon name="flag" size={13} className="shrink-0" />
        What is wrong
        <span className="text-ink-muted font-normal">
          {[who && `Reported by ${who}`, row.reported_at && formatDate(row.reported_at)]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </p>
      <p className="text-ink mt-1 text-sm break-words whitespace-pre-line">
        {row.reported_reason?.trim() || 'No reason was given.'}
      </p>
    </div>
  );
}
